/**
 * Theme presets — client mirror of server/site-cms/theme.ts. Keep them in
 * sync when adding palettes or font pairs. (Same duplication pattern already
 * used between src/lib/api.ts and server/site-cms/schemas.ts.)
 */

export type PaletteTokens = {
  "--background": string;
  "--foreground": string;
  "--card": string;
  "--card-foreground": string;
  "--primary": string;
  "--primary-foreground": string;
  "--secondary": string;
  "--secondary-foreground": string;
  "--muted": string;
  "--muted-foreground": string;
  "--accent": string;
  "--accent-foreground": string;
  "--destructive": string;
  "--destructive-foreground": string;
  "--border": string;
  "--input": string;
  "--ring": string;
  "--surface-dark": string;
};

export interface Palette {
  id: string;
  label: string;
  swatches: [string, string, string, string];
  tokens: PaletteTokens;
}

export const PALETTES: readonly Palette[] = [
  {
    id: "classic-burgundy",
    label: "ברנדי קלאסי",
    swatches: ["#6f2a3b", "#f9f7f5", "#c9a878", "#2c2421"],
    tokens: {
      "--background": "30 25% 97%",
      "--foreground": "17 14% 14%",
      "--card": "0 0% 100%",
      "--card-foreground": "17 14% 14%",
      "--primary": "347 45% 30%",
      "--primary-foreground": "30 25% 97%",
      "--secondary": "30 18% 92%",
      "--secondary-foreground": "17 14% 14%",
      "--muted": "30 25% 94%",
      "--muted-foreground": "17 8% 38%",
      "--accent": "30 35% 65%",
      "--accent-foreground": "17 14% 14%",
      "--destructive": "0 65% 40%",
      "--destructive-foreground": "30 25% 97%",
      "--border": "30 15% 82%",
      "--input": "30 15% 82%",
      "--ring": "347 45% 30%",
      "--surface-dark": "17 14% 14%",
    },
  },
  {
    id: "olive-stone",
    label: "זית ואבן",
    swatches: ["#4a5d3a", "#f4f1ea", "#b8956a", "#2a2a25"],
    tokens: {
      "--background": "40 22% 95%",
      "--foreground": "60 10% 15%",
      "--card": "0 0% 100%",
      "--card-foreground": "60 10% 15%",
      "--primary": "90 22% 30%",
      "--primary-foreground": "40 22% 95%",
      "--secondary": "40 15% 90%",
      "--secondary-foreground": "60 10% 15%",
      "--muted": "40 20% 92%",
      "--muted-foreground": "60 8% 40%",
      "--accent": "35 35% 60%",
      "--accent-foreground": "60 10% 15%",
      "--destructive": "0 60% 40%",
      "--destructive-foreground": "40 22% 95%",
      "--border": "40 12% 80%",
      "--input": "40 12% 80%",
      "--ring": "90 22% 30%",
      "--surface-dark": "60 10% 15%",
    },
  },
  {
    id: "terracotta-cream",
    label: "טרקוטה וקרם",
    swatches: ["#a04a2b", "#fbf6ee", "#d4a373", "#3a2519"],
    tokens: {
      "--background": "38 50% 96%",
      "--foreground": "20 30% 16%",
      "--card": "0 0% 100%",
      "--card-foreground": "20 30% 16%",
      "--primary": "15 58% 40%",
      "--primary-foreground": "38 50% 96%",
      "--secondary": "38 35% 90%",
      "--secondary-foreground": "20 30% 16%",
      "--muted": "38 40% 93%",
      "--muted-foreground": "20 18% 38%",
      "--accent": "30 50% 65%",
      "--accent-foreground": "20 30% 16%",
      "--destructive": "0 70% 40%",
      "--destructive-foreground": "38 50% 96%",
      "--border": "30 20% 80%",
      "--input": "30 20% 80%",
      "--ring": "15 58% 40%",
      "--surface-dark": "20 30% 16%",
    },
  },
  {
    id: "forest-sand",
    label: "יער וחול",
    swatches: ["#2f4a3a", "#f1ede4", "#caa97a", "#1b231d"],
    tokens: {
      "--background": "40 28% 93%",
      "--foreground": "140 18% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "140 18% 12%",
      "--primary": "145 25% 25%",
      "--primary-foreground": "40 28% 93%",
      "--secondary": "40 18% 88%",
      "--secondary-foreground": "140 18% 12%",
      "--muted": "40 22% 90%",
      "--muted-foreground": "140 8% 35%",
      "--accent": "35 35% 62%",
      "--accent-foreground": "140 18% 12%",
      "--destructive": "0 65% 40%",
      "--destructive-foreground": "40 28% 93%",
      "--border": "40 12% 78%",
      "--input": "40 12% 78%",
      "--ring": "145 25% 25%",
      "--surface-dark": "140 18% 12%",
    },
  },
  {
    id: "midnight-gold",
    label: "חצות וזהב",
    swatches: ["#1c2a45", "#f3eed6", "#c9a23a", "#0d1525"],
    tokens: {
      "--background": "48 38% 92%",
      "--foreground": "220 35% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "220 35% 12%",
      "--primary": "220 40% 22%",
      "--primary-foreground": "48 38% 92%",
      "--secondary": "48 25% 88%",
      "--secondary-foreground": "220 35% 12%",
      "--muted": "48 30% 90%",
      "--muted-foreground": "220 15% 35%",
      "--accent": "44 56% 50%",
      "--accent-foreground": "220 35% 12%",
      "--destructive": "0 65% 40%",
      "--destructive-foreground": "48 38% 92%",
      "--border": "44 12% 78%",
      "--input": "44 12% 78%",
      "--ring": "220 40% 22%",
      "--surface-dark": "220 35% 12%",
    },
  },
] as const;

export function getPalette(id: string): Palette {
  return PALETTES.find((p) => p.id === id) ?? PALETTES[0];
}

/* ─── font pairs ─────────────────────────────────────────────────────── */

export interface FontPair {
  id: string;
  label: string;
  headingFamily: string;
  bodyFamily: string;
  googleFontsParam: string;
}

export const FONT_PAIRS: readonly FontPair[] = [
  {
    id: "heebo-frank",
    label: "Heebo + Frank Ruhl Libre",
    headingFamily: '"Frank Ruhl Libre", Georgia, serif',
    bodyFamily: '"Heebo", system-ui, sans-serif',
    googleFontsParam:
      "Heebo:wght@300;400;500;600;700&family=Frank+Ruhl+Libre:wght@500;700",
  },
  {
    id: "assistant-david",
    label: "Assistant + David Libre",
    headingFamily: '"David Libre", Georgia, serif',
    bodyFamily: '"Assistant", system-ui, sans-serif',
    googleFontsParam:
      "Assistant:wght@300;400;500;600;700&family=David+Libre:wght@500;700",
  },
  {
    id: "rubik-suez",
    label: "Rubik + Suez One",
    headingFamily: '"Suez One", Georgia, serif',
    bodyFamily: '"Rubik", system-ui, sans-serif',
    googleFontsParam: "Rubik:wght@300;400;500;600;700&family=Suez+One",
  },
  {
    id: "varela-amatic",
    label: "Varela Round + Amatic SC",
    headingFamily: '"Amatic SC", Georgia, serif',
    bodyFamily: '"Varela Round", system-ui, sans-serif',
    googleFontsParam: "Varela+Round&family=Amatic+SC:wght@400;700",
  },
] as const;

export function getFontPair(id: string): FontPair {
  return FONT_PAIRS.find((p) => p.id === id) ?? FONT_PAIRS[0];
}

/* ─── button style ───────────────────────────────────────────────────── */

export const BUTTON_SHAPES = ["pill", "rounded", "square"] as const;
export type ButtonShape = (typeof BUTTON_SHAPES)[number];

export const BUTTON_SHAPE_RADIUS: Record<ButtonShape, string> = {
  pill: "9999px",
  rounded: "0.75rem",
  square: "0.25rem",
};

export const BUTTON_SHAPE_LABEL: Record<ButtonShape, string> = {
  pill: "גלולה",
  rounded: "מעוגל",
  square: "מרובע",
};

export const BUTTON_FILLS = ["solid", "outline", "ghost"] as const;
export type ButtonFill = (typeof BUTTON_FILLS)[number];

export const BUTTON_FILL_LABEL: Record<ButtonFill, string> = {
  solid: "מלא",
  outline: "מסגרת",
  ghost: "שקוף",
};

/* ─── per-token overrides (all 18 palette tokens) ────────────────────── */

export const OVERRIDE_KEYS = [
  "--background",
  "--foreground",
  "--card",
  "--card-foreground",
  "--primary",
  "--primary-foreground",
  "--secondary",
  "--secondary-foreground",
  "--muted",
  "--muted-foreground",
  "--accent",
  "--accent-foreground",
  "--destructive",
  "--destructive-foreground",
  "--border",
  "--input",
  "--ring",
  "--surface-dark",
] as const;
export type OverrideKey = (typeof OVERRIDE_KEYS)[number];

export const OVERRIDE_KEY_LABEL: Record<OverrideKey, string> = {
  "--background": "רקע",
  "--foreground": "טקסט",
  "--card": "רקע כרטיסים",
  "--card-foreground": "טקסט בכרטיסים",
  "--primary": "צבע ראשי",
  "--primary-foreground": "טקסט על צבע ראשי",
  "--secondary": "צבע משני",
  "--secondary-foreground": "טקסט על צבע משני",
  "--muted": "רקע עמום",
  "--muted-foreground": "טקסט עמום",
  "--accent": "צבע דגש",
  "--accent-foreground": "טקסט על דגש",
  "--destructive": "אזהרה/מחיקה",
  "--destructive-foreground": "טקסט על אזהרה",
  "--border": "מסגרות",
  "--input": "שדות קלט",
  "--ring": "focus ring",
  "--surface-dark": "רקע כהה (פוטר)",
};

/** One-liner description of where each token actually appears on the site
 *  — shown under the label in the admin so it's clear what each picker
 *  controls. */
export const OVERRIDE_KEY_HINT: Record<OverrideKey, string> = {
  "--background": "רקע כל העמוד — בקטעים ללא קטע מעוצב",
  "--foreground": "צבע הטקסט הראשי בעמוד",
  "--card": "רקע של כרטיסי תוכן (יינות, מארזים, סיורים, חלונות באדמין)",
  "--card-foreground": "טקסט בתוך כרטיסים",
  "--primary": "צבע ראשי — כפתורים, כותרות eyebrow, קישורים, צבע המותג",
  "--primary-foreground": "טקסט בתוך כפתורים מלאים בצבע הראשי",
  "--secondary": "רקע משני — סטטוסים, badges, אלמנטים פחות בולטים",
  "--secondary-foreground": "טקסט על רקע משני",
  "--muted": "רקעים עדינים — קטעי 'יינות נבחרים', כרטיסי משנה",
  "--muted-foreground": "טקסט עמום — תיאורים, captions, label-ים",
  "--accent": "דגש — gradient של hero, אייקונים, חלקי קישוט",
  "--accent-foreground": "טקסט על דגש",
  "--destructive": "אדום — כפתורי מחיקה, הודעות שגיאה",
  "--destructive-foreground": "טקסט בתוך כפתורי מחיקה",
  "--border": "צבע כל המסגרות והקווים המפרידים באתר",
  "--input": "צבע מסגרת שדות קלט",
  "--ring": "מסגרת focus בעת ניווט מקלדת (פעם בשעה אולי)",
  "--surface-dark": "רקע הפוטר וכותרת hero ההיא חוף הים",
};

/** Groups for the admin UI — primary tokens get prominence. */
export const OVERRIDE_GROUPS: { title: string; tokens: OverrideKey[] }[] = [
  {
    title: "צבעים עיקריים",
    tokens: ["--background", "--foreground", "--primary", "--primary-foreground"],
  },
  {
    title: "צבעי דגש",
    tokens: ["--accent", "--accent-foreground", "--secondary", "--secondary-foreground"],
  },
  {
    title: "רקעים וטקסטים משניים",
    tokens: ["--card", "--card-foreground", "--muted", "--muted-foreground"],
  },
  {
    title: "מסגרות ואינטראקציה",
    tokens: ["--border", "--input", "--ring", "--surface-dark"],
  },
  {
    title: "אזהרות",
    tokens: ["--destructive", "--destructive-foreground"],
  },
];

/* ─── individual font families (for free heading/body picks) ─────────── */

export interface FontFamily {
  id: string;
  label: string;
  family: string;
  /** Visual category for grouping the dropdown. */
  category: "serif" | "sans" | "display";
}

export const FONT_FAMILIES: readonly FontFamily[] = [
  // Serif / classical
  { id: "frank-ruhl-libre", label: "Frank Ruhl Libre", family: '"Frank Ruhl Libre", Georgia, serif', category: "serif" },
  { id: "david-libre", label: "David Libre", family: '"David Libre", Georgia, serif', category: "serif" },
  { id: "bellefair", label: "Bellefair", family: '"Bellefair", Georgia, serif', category: "serif" },
  // Display / impact
  { id: "suez-one", label: "Suez One", family: '"Suez One", Georgia, serif', category: "display" },
  { id: "amatic-sc", label: "Amatic SC", family: '"Amatic SC", Georgia, serif', category: "display" },
  { id: "secular-one", label: "Secular One", family: '"Secular One", system-ui, sans-serif', category: "display" },
  // Sans / modern
  { id: "heebo", label: "Heebo", family: '"Heebo", system-ui, sans-serif', category: "sans" },
  { id: "assistant", label: "Assistant", family: '"Assistant", system-ui, sans-serif', category: "sans" },
  { id: "rubik", label: "Rubik", family: '"Rubik", system-ui, sans-serif', category: "sans" },
  { id: "varela-round", label: "Varela Round", family: '"Varela Round", system-ui, sans-serif', category: "sans" },
  { id: "karantina", label: "Karantina", family: '"Karantina", system-ui, sans-serif', category: "sans" },
  { id: "alef", label: "Alef", family: '"Alef", system-ui, sans-serif', category: "sans" },
] as const;

export const FONT_CATEGORY_LABEL: Record<FontFamily["category"], string> = {
  serif: "סריף",
  sans: "ללא סריף",
  display: "תצוגתי",
};

export function getFontFamily(id: string | undefined): FontFamily | null {
  if (!id) return null;
  return FONT_FAMILIES.find((f) => f.id === id) ?? null;
}

/* ─── persisted theme shape ──────────────────────────────────────────── */

export interface SiteTheme {
  paletteId: string;
  fontPairId: string;
  /** Overrides for heading + body fonts (any of FONT_FAMILIES). When set,
   *  takes precedence over the font-pair selection. */
  headingFont?: string;
  bodyFont?: string;
  buttonShape: ButtonShape;
  buttonFill: ButtonFill;
  overrides: Partial<Record<OverrideKey, string>>;
}

export const DEFAULT_THEME: SiteTheme = {
  paletteId: "classic-burgundy",
  fontPairId: "heebo-frank",
  buttonShape: "pill",
  buttonFill: "solid",
  overrides: {},
};
